import { HalfFloatType, type PerspectiveCamera, type Scene, type WebGLRenderer } from 'three';
import {
  BloomEffect,
  BrightnessContrastEffect,
  EffectComposer,
  EffectPass,
  HueSaturationEffect,
  KernelSize,
  RenderPass,
  SMAAEffect,
  SMAAPreset,
  ToneMappingEffect,
  ToneMappingMode,
  VignetteEffect,
} from 'postprocessing';
import { BLOOM, RENDER } from '../config';
import { ExposureEffect } from './ExposureEffect';

/**
 * Post-processing řetězec.
 *
 * Rozdělení do tří průchodů není plýtvání, ale nutnost:
 *
 *  - BloomEffect i SMAAEffect si čtou **vstupní buffer svého průchodu**, ne
 *    barvu zřetězenou z předchozích efektů. Cokoliv, co je musí ovlivnit,
 *    proto musí proběhnout v dřívějším průchodu. Expozice ve stejném průchodu
 *    jako bloom nedělá nic — bloom by pořád svítil z neexponovaného HDR.
 *  - SMAA musí analyzovat už tone-mapovaný obraz, jinak v HDR rozsahu
 *    nenajde hrany tam, kde je oko vidí.
 *
 * Pořadí je tedy: expozice (HDR) → bloom + tone mapping + grading → SMAA.
 *
 * SSAO tu zatím není schválně — na holém terénu bez objektů skoro nic nepřidá
 * a stojí depth/normal pass. Přidáme ho, až přijdou postavy, stromy a budovy.
 */
export class PostFX {
  readonly composer: EffectComposer;
  private readonly bloom: BloomEffect;
  private readonly exposureEffect: ExposureEffect;

  constructor(renderer: WebGLRenderer, scene: Scene, camera: PerspectiveCamera) {
    this.composer = new EffectComposer(renderer, { frameBufferType: HalfFloatType });
    this.composer.addPass(new RenderPass(scene, camera));

    this.exposureEffect = new ExposureEffect(RENDER.exposure);
    this.composer.addPass(new EffectPass(camera, this.exposureEffect));

    this.bloom = new BloomEffect({
      mipmapBlur: true,
      luminanceThreshold: BLOOM.threshold,
      luminanceSmoothing: BLOOM.smoothing,
      intensity: BLOOM.intensity,
      radius: BLOOM.radius,
      kernelSize: KernelSize.MEDIUM,
    });

    const toneMapping = new ToneMappingEffect({ mode: ToneMappingMode.ACES_FILMIC });
    const grade = new HueSaturationEffect({ saturation: 0.1 });
    const contrast = new BrightnessContrastEffect({ contrast: 0.06, brightness: -0.01 });
    const vignette = new VignetteEffect({ offset: 0.36, darkness: 0.4 });

    this.composer.addPass(new EffectPass(camera, this.bloom, toneMapping, grade, contrast, vignette));
    this.composer.addPass(new EffectPass(camera, new SMAAEffect({ preset: SMAAPreset.HIGH })));
  }

  setSize(width: number, height: number): void {
    this.composer.setSize(width, height);
  }

  render(delta: number): void {
    this.composer.render(delta);
  }

  get exposure(): number {
    return this.exposureEffect.exposure;
  }

  setExposure(value: number): void {
    this.exposureEffect.exposure = value;
  }

  setBloomIntensity(value: number): void {
    this.bloom.intensity = value;
  }

  dispose(): void {
    this.composer.dispose();
  }
}
